import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/hooks/auth/useAuth";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { z } from "zod";
import { Turnstile } from "@marsidev/react-turnstile";
import { useState } from "react";

const recoverSchema = z.object({
  email: z.string().email("Email invalide").nonempty("Email requis"),
});

type RecoverFormData = z.infer<typeof recoverSchema>;

export function RecoverPassword() {
  const form = useForm<RecoverFormData>({
    resolver: zodResolver(recoverSchema),
  });
  const navigate = useNavigate();
  const { resetPassword } = useAuth();
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async (data: RecoverFormData) => {
      const result = await resetPassword(data.email, turnstileToken);
      if (result.error) throw new Error(result.error.message);
    },
    onError: (error: Error) => {
      if (error.message?.includes('captcha_failed')) {
        toast.error("Échec de la vérification CAPTCHA", {
          description: "Veuillez réessayer.",
        });
        setTurnstileToken(null);
      } else {
        toast.error(
          error.message || "Impossible d'envoyer l'email de réinitialisation.",
        );
      }
    },
    onSuccess: () => {
      toast.success(
        "Vérifiez votre boîte mail pour réinitialiser votre mot de passe.",
      );
      navigate("/");
    },
  });

  const onSubmit = async (data: RecoverFormData) => {
    if (!turnstileToken) {
      toast.error("Veuillez compléter la vérification Turnstile");
      return;
    }
    await mutation.mutateAsync(data);
  };

  return (
    <div className="gradient-bg flex min-h-full flex-col px-4 py-6">
      <div className="mx-auto w-full max-w-md">
        <div className="mb-6 flex items-center gap-4">
          <Button
            variant="ghost"
            className="p-2"
            onClick={() => navigate(-1)}
            aria-label="Retour"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-lg font-semibold text-gray-700">
            Mot de passe oublié
          </h1>
        </div>
        <Card className="backdrop-blur-xs rounded-2xl border border-gray-100 bg-white/90 pt-4 shadow-lg">
          <CardContent>
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-4"
              >
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          id="email"
                          type="email"
                          placeholder="votre@email.com"
                          autoFocus
                          className={mutation.error ? "border-red-500" : ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <div className="flex w-full justify-center">
                  <Turnstile
                    siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY}
                    onSuccess={setTurnstileToken}
                    onExpire={() => setTurnstileToken(null)}
                    onError={() => setTurnstileToken(null)}
                    options={{
                      theme: "light",
                    }}
                  />
                </div>
                
                {mutation.error && (
                  <p className="mt-2 text-sm text-red-500">
                    {mutation.error.message}
                  </p>
                )}
                <Button
                  type="submit"
                  className="w-full py-2.5 text-base"
                  disabled={mutation.isPending || !turnstileToken}
                >
                  {mutation.isPending ?
                    "Envoi en cours..."
                  : "Récupérer mon mot de passe"}
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default RecoverPassword;
